import React from 'react';
import { autocompleteAirports, resolveAirport, type AirportResolution, type AirportSuggestion } from '../lib/api';

type Props = {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  showResolved?: boolean;
};

const AirportAutocompleteInput: React.FC<Props> = ({ value, onChange, placeholder, showResolved = false }) => {
  const [suggestions, setSuggestions] = React.useState<AirportSuggestion[]>([]);
  const [open, setOpen] = React.useState(false);
  const [resolved, setResolved] = React.useState<AirportResolution | null>(null);
  const [resolving, setResolving] = React.useState(false);
  const [resolveError, setResolveError] = React.useState<string | null>(null);

  React.useEffect(() => {
    let active = true;
    const id = window.setTimeout(async () => {
      try {
        const rows = await autocompleteAirports(value);
        if (active) setSuggestions(rows);
      } catch {
        if (active) setSuggestions([]);
      }
    }, 250);
    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [value]);

  React.useEffect(() => {
    if (!showResolved) return;
    const q = value.trim();
    if (!q || q.length < 2) {
      setResolved(null);
      setResolveError(null);
      return;
    }

    let active = true;
    const id = window.setTimeout(async () => {
      try {
        setResolving(true);
        setResolveError(null);
        const res = await resolveAirport(q);
        if (!active) return;
        setResolved(res);
      } catch (e) {
        if (!active) return;
        setResolveError(e instanceof Error ? e.message : 'Failed to resolve airport');
        setResolved(null);
      } finally {
        if (active) setResolving(false);
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(id);
    };
  }, [value, showResolved]);

  return (
    <div style={{ position: 'relative' }}>
      <input
        value={value}
        onChange={(e) => {
          onChange(e.target.value.toUpperCase());
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        placeholder={placeholder}
      />
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 20,
            background: 'white',
            border: '1px solid var(--border)',
            borderRadius: '0.65rem',
            maxHeight: 240,
            overflowY: 'auto',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {suggestions.map((item) => (
            <button
              key={item.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(item.id);
                setOpen(false);
              }}
              style={{
                width: '100%',
                textAlign: 'left',
                padding: '0.55rem 0.7rem',
                border: 'none',
                background: 'white',
                borderBottom: '1px solid #f1f5f9',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 700 }}>{item.id} - {item.name}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{item.city}</div>
            </button>
          ))}
        </div>
      )}
      {showResolved && (
        <div style={{ marginTop: '0.4rem', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          {resolving ? (
            <>Resolving…</>
          ) : resolveError ? (
            <span style={{ color: '#dc2626' }}>{resolveError}</span>
          ) : resolved ? (
            <>
              Resolved: <strong>{resolved.resolvedId}</strong> — {resolved.resolvedName}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default AirportAutocompleteInput;
